/**
 * The synthetic "All Portfolios" aggregate: a reserved portfolio id that
 * combines every portfolio into one view valued in the global base currency.
 * Callers force FX on for this view since combining funds in different
 * currencies only makes sense once converted. Pure.
 */
import type { Settings, Trade } from './portfolioApi';
import { fxRateFor, type FxStore } from './fx';
import { resolvePortfolioStartingCash } from './portfolioApi';

export const ALL_PORTFOLIOS_ID = '__all__';
export const ALL_PORTFOLIOS_LABEL = 'All Portfolios';

export function isAllPortfolios(id: string | null | undefined): boolean {
  return id === ALL_PORTFOLIOS_ID;
}

export interface AggregatedPortfolio {
  trades: Trade[];
  startingCash: number;
  base: string;
}

export function aggregateAllPortfolios(
  settings: Settings,
  tradesByPortfolio: Record<string, Trade[]>,
  fxStore: FxStore,
): AggregatedPortfolio {
  const base = settings.baseCurrency ?? 'USD';
  const portfolios = settings.portfolios ?? [];

  const trades: Trade[] = [];
  let startingCash = 0;
  for (const p of portfolios) {
    for (const t of tradesByPortfolio[p.id] ?? []) trades.push(t);
    const pCash = resolvePortfolioStartingCash(settings, p.id);
    const pBase = p.baseCurrency ?? base;
    // Opening cash has no single trade date; convert at the latest rate. A
    // fund whose base has no rate to this one is left out (fxRateFor's 0) and
    // named by missingFxCurrencies, never counted at 1.
    startingCash += pCash * fxRateFor({ store: fxStore, base }, pBase, null);
  }

  return { trades, startingCash, base };
}
